import React, { useState, useEffect } from "react";
import UserGroupTable from "../UserGroupTable/UserGroupTable";
import UserGroupForm from "../UserGroupForm/UserGroupForm";
import ErrorDialog from "../../../ErrorDialog/ErrorDialog";
import Dialog from "../../../FormDialog/Dialog";
import { getHeaders } from "../../../../ApiHeaders/apiHeadersEntitlements";
import LoadingStyle from "../../../../Loading/Loading";
import { SplitDivider, useResizableSplit } from "../../../../SplitPane/ResizableSplit";
import {
  EXTERNAL_TENANT_USERS_QUERY,
  isAmpEntitlementDomain,
  isExternalTenantUser,
} from "../../../constants/entitlementDomains";


interface UserGroupSetupProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  /** Admin on this page: may create, edit and delete. Otherwise the page is view-only. */
  canDelete?: boolean;
}

interface Role {
  role_id: number;
  role_name: string;
  domain: string;
  description?: string;
}

interface RoleUserMapping {
  role_id: number;
  user_id: number;
}

interface User {
  user_id: number;
  login_name: string;
  email: string;
}


const UserGroupSetup: React.FC<UserGroupSetupProps> = ({
  registerTabSwitchGuard,
  canDelete = false,
}) => {
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const apiUrl = process.env.REACT_APP_ENTITLEMENTS_API;
  const [selectedUserGroup, setSelectedUserGroup] = useState<any | null>(null);
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [error, setError] = useState<{ title: string; details: string } | null>(
    null
  );
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [selectedPogs, setSelectedPogs] = useState<string[]>([]);
  
  useEffect(() => {
    if (!selectedUserGroup) return;
  
    const loadMappings = async () => {
      const headers = await getHeaders();
  
      try {
        // COLLECTIONS
        const rcRes = await fetch(`${apiUrl}/role-collection-mapping`, { headers });
        const rcData = await rcRes.json();
  
        const roleCollections = rcData.response.data
          .filter((m: any) => m.role_id === selectedUserGroup.user_group_id)
          .map((m: any) => String(m.collection_id));
  
        setSelectedCollections(roleCollections);
  
        // POGS
        const rpRes = await fetch(`${apiUrl}/role-pog-mapping`, { headers });
        const rpData = await rpRes.json();
  
        const rolePogs = rpData.response.data
          .filter((m: any) => m.role_id === selectedUserGroup.user_group_id)
          .map((m: any) => String(m.permission_object_group_id));
  
        setSelectedPogs(rolePogs);
  
      } catch (err) {
        handleApiError(err, "Failed to load mappings");
      }
    };
  
    loadMappings();
  }, [selectedUserGroup]);

  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };
  useEffect(() => {
    if (registerTabSwitchGuard) {
      registerTabSwitchGuard(() => {
        return new Promise<boolean>((resolve) => {
          if (isFormDirty || isCreateMode) {
            setDialog({
              open: true,
              title: "Unsaved Changes",
              message:
                "You have unsaved changes. Are you sure you want to discard them?",
              confirmButtonText: "Yes",
              cancelButtonText: "No",
              onConfirm: () => {
                setIsFormDirty(false);
                setIsCreateMode(false);
                closeDialog();
                resolve(true);
              },
              onCancel: () => {
                closeDialog();
                resolve(false);
              },
            });
          } else {
            resolve(true);
          }
        });
      });
    }
  }, [isFormDirty, isCreateMode]);
  // const handleApiError = (err: any, fallbackTitle: string) => {
  //   let title = fallbackTitle;
  //   let details = "An unknown error occurred";

  //   if (err.response?.data?.error) {
  //     title = err.response.data.error.title || fallbackTitle;
  //     details = err.response.data.error.error.message || details;
  //   } else if (err.message) {
  //     if (err.message.includes("Network Error") || err.message.includes("timeout")) {
  //       title = "Network Error";
  //       details = "Unable to connect to the server. Check your internet connection.";
  //     } else {
  //       details = err.message;
  //     }
  //   }
  //   setError({ title, details });
  // };

  const handleApiError = (err: any, fallbackTitle: string) => {
    let title = fallbackTitle;
    let details = "An unknown error occurred";

    const statusCode = err.response?.status;
    const errorResponse = err.response?.data?.error;

    const backendMessage =
      errorResponse?.error?.message || errorResponse?.message;

    switch (statusCode) {
      case 400:
        title = "Bad Request";
        details = backendMessage || "The data you submitted is invalid.";
        break;
      case 401:
        title = "Acces Denied";
        details =
          "You're not authorized to access this content. Please log in or check your access permissions.";
        break;
      case 403:
        title = "Access Denied";
        details = "You need permission to access this site.";
        break;
      case 404:
        title = "Not Found";
        details =
          backendMessage || "The requested resource could not be found.";
        break;
      case 500:
        title = "Internal Server Error";
        details =
          backendMessage ||
          "Server error while fetching data. Please try again later.";
        break;
      default:
        if (
          err.message?.includes("Network Error") ||
          err.message?.includes("timeout")
        ) {
          title = "Network Error";
          details =
            "Unable to connect to the server. Check your internet connection.";
        } else {
          title = fallbackTitle;
          details =
            backendMessage || err.message || "An unexpected error occurred.";
        }
        break;
    }

    setError({ title, details });
    // datadogRum.addError(err, { title, details });
  };

  const buildUserGroups = (
    roles: Role[],
    mappings: RoleUserMapping[],
    users: User[]
  ) => {
    const userMap = new Map<number, User>();
    users.forEach(u => userMap.set(Number(u.user_id), u));
  
    const groupMap = new Map<number, any>();
    roles.forEach(role => {
      groupMap.set(Number(role.role_id), {
        user_group_id: role.role_id,
        user_group_name: role.role_name,
        domain: role.domain,
        description: role.description,
        users: []
      });
    });
  
    mappings.forEach(map => {
      const roleId = Number(map.role_id);
      const userId = Number(map.user_id);
  
      const group = groupMap.get(roleId);
      const user = userMap.get(userId);
  
      if (group && user) {
        group.users.push({
          user_id: user.user_id,
          login_name: user.login_name,
          email: user.email
        });
      }
    });
  
    return Array.from(groupMap.values());
  };
  
  
  
  const fetchUserGroups = async () => {
    setLoading(true);
    const headers = await getHeaders();
  
    try {
      const [rolesRes, mappingsRes, usersRes] = await Promise.all([
        fetch(`${apiUrl}/role`, { headers }),
        fetch(`${apiUrl}/role-user-mapping`, { headers }),
        fetch(`${apiUrl}/user/?${EXTERNAL_TENANT_USERS_QUERY}`, { headers }),
      ]);
  
      const roles = (await rolesRes.json()).response.data.filter((r: Role) =>
        isAmpEntitlementDomain(r.domain)
      );
      const mappings = (await mappingsRes.json()).response.data;
      const users = (await usersRes.json()).response.data.filter(
        isExternalTenantUser
      );
  
      const transformedGroups = buildUserGroups(roles, mappings, users);
  
      setUserGroups(transformedGroups);
  
    } catch (err) {
      handleApiError(err, "Failed to Fetch Roles");
    } finally {
      setLoading(false);
    }
  };
  
  
  

  // const fetchUserGroups = async () => {
  //   const headers = await getHeaders();
  //   try {
  //     const response = await fetch(`${apiUrl}/legacy/user-group-mappings/`, {
  //       headers,
  //     });
  //     const data = await response.json();
  //     if (data.response && data.response.data) {
  //       setUserGroups(data.response.data);
  //     }
  //   } catch (err) {
  //     handleApiError(err, "Failed to Fetch Roles");
  //   }
  // };

  useEffect(() => {
    fetchUserGroups();
  }, []);

  // const handleCreateUserGroup = async (newUserGroup: any) => {
  //   const baseHeaders = await getHeaders();

  //   const headers = {
  //     "Content-Type": "application/json",
  //     ...baseHeaders,
  //   };

  //   try {
  //     const response = await fetch(`${apiUrl}/user-group-mappings/`, {
  //       method: "POST",
  //       headers,
  //       body: JSON.stringify(newUserGroup),
  //     });
  //     if (response.ok) {
  //       fetchUserGroups();
  //       setDialog({
  //         open: true,
  //         title: "Success",
  //         message: `Role "${newUserGroup.user_group_name}" successfully created.`,
  //         confirmButtonText: "Close",
  //         onConfirm: closeDialog,
  //       });
  //     } else {
  //       throw new Error("Failed to create user group");
  //     }
  //   } catch (err) {
  //     handleApiError(err, "Failed to Create Role");
  //   }
  // };

  const handleCreateUserGroup = async (newUserGroup: any) => {
    const baseHeaders = await getHeaders();
  
    const headers = {
      "Content-Type": "application/json",
      ...baseHeaders,
    };
  
    // Role-collection and role-POG mappings are keyed by the ROLE's domain
    // (a mobile role's MOBILE-APP-CORE grant is stored under 'mobile').
    const roleDomain: string = newUserGroup.domain || "amp";

    try {
      // 1️⃣ Create Role
      const roleResponse = await fetch(`${apiUrl}/role`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          role_name: newUserGroup.user_group_name,
          domain: roleDomain,
          description: newUserGroup.description || "",
          enabled: "Y",
          company_id: 0,
          sys_created_by: newUserGroup.sys_created_by,
          sys_modified_by: newUserGroup.sys_modified_by,
        }),
      });
  
      if (!roleResponse.ok) {
        throw new Error("Failed to create role");
      }
  
      const roleData = await roleResponse.json();
      const roleId = roleData.response.data.role_id;
  
      console.log("Created role ID:", roleId);
  
      // 2️⃣ Create role-user mappings
      const mappingResponses = await Promise.all(
        newUserGroup.user_ids.map((userId: number) =>
          fetch(`${apiUrl}/role-user-mapping`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              role_id: roleId,
              user_id: userId,
              enabled: "Y",
              sys_created_by: newUserGroup.sys_created_by,
              sys_modified_by: newUserGroup.sys_modified_by,
            }),
          })
        )
      );
  
      // 3️⃣ Validate all mappings succeeded
      mappingResponses.forEach((res) => {
        if (!res.ok) {
          throw new Error("Failed to create role-user mapping");
        }
      });

      // CREATE COLLECTION MAPPINGS
      await Promise.all(
        selectedCollections.map(id =>
          fetch(`${apiUrl}/role-collection-mapping`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              role_id: roleId,
              collection_id: Number(id),
              domain: roleDomain,
              description: "",
              enabled: "Y",
              sys_created_by: newUserGroup.sys_created_by,
              sys_modified_by: newUserGroup.sys_modified_by,
            }),
          })
        )
      );

      // CREATE POG MAPPINGS
      await Promise.all(
        selectedPogs.map(id =>
          fetch(`${apiUrl}/role-pog-mapping`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              role_id: roleId,
              permission_object_group_id: Number(id),
              domain: roleDomain,
              access_level_name: "read",
              description: "",
              enabled: "Y",
              sys_created_by: newUserGroup.sys_created_by,
              sys_modified_by: newUserGroup.sys_modified_by,
            }),
          })
        )
      );
  
      // 4️⃣ Refresh UI
      await fetchUserGroups();
  
      setDialog({
        open: true,
        title: "Success",
        message: `Role "${newUserGroup.user_group_name}" successfully created.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err) {
      handleApiError(err, "Failed to Create Role");
    }
  };
  
  

  // const handleSaveUserGroup = async (
  //   userGroupId: string,
  //   updatedUserGroup: any
  // ) => {
  //   const baseHeaders = await getHeaders();

  //   const headers = {
  //     "Content-Type": "application/json",
  //     ...baseHeaders,
  //   };

  //   try {
  //     // First, update user_group_name
  //     const nameUpdatePayload = {
  //       user_group_name: updatedUserGroup.user_group_name,
  //       description: updatedUserGroup.description || "",
  //       enabled: updatedUserGroup.enabled || "true",
  //       sys_created_by: updatedUserGroup.sys_created_by || "admin",
  //       sys_modified_by: updatedUserGroup.sys_modified_by || "admin",
  //     };

  //     const nameResponse = await fetch(`${apiUrl}/user-group/${userGroupId}`, {
  //       method: "PUT",
  //       headers,
  //       body: JSON.stringify(nameUpdatePayload),
  //     });

  //     if (!nameResponse.ok) {
  //       throw new Error("Failed to update user_group_name");
  //     }

  //     // Second, update the rest via /user-group-mappings/{id}
  //     const mappingResponse = await fetch(
  //       `${apiUrl}/user-group-mappings/${userGroupId}`,
  //       {
  //         method: "PUT",
  //         headers,
  //         body: JSON.stringify(updatedUserGroup),
  //       }
  //     );

  //     if (!mappingResponse.ok) {
  //       throw new Error("Failed to update user group mappings");
  //     }

  //     fetchUserGroups();
  //     setDialog({
  //       open: true,
  //       title: "Success",
  //       message: `Role "${updatedUserGroup.user_group_name}" successfully updated.`,
  //       confirmButtonText: "Close",
  //       onConfirm: closeDialog,
  //     });
  //   } catch (err) {
  //     handleApiError(err, "Failed to Update Role");
  //   }
  // };

  const handleSaveUserGroup = async (
    roleId: number,
    updatedUserGroup: any
  ) => {
    const baseHeaders = await getHeaders();
  
    const headers = {
      "Content-Type": "application/json",
      ...baseHeaders,
    };
  
    const existingGroup = userGroups.find(
      (g) => g.user_group_id === roleId
    );

    // A role's domain is fixed once created: its mappings are keyed by it.
    const roleDomain: string = existingGroup?.domain || "amp";

    try {
      // =========================================================
      // 1️⃣ UPDATE ROLE BASIC INFO
      // =========================================================
      const roleResponse = await fetch(`${apiUrl}/role/${roleId}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          role_name: updatedUserGroup.user_group_name,
          domain: roleDomain,
          description: updatedUserGroup.description || "",
          enabled: "Y",
          company_id: 0,
          sys_created_by: updatedUserGroup.sys_created_by,
          sys_modified_by: updatedUserGroup.sys_modified_by,
        }),
      });
  
      if (!roleResponse.ok) {
        throw new Error("Failed to update role");
      }
  
      // =========================================================
      // 2️⃣ USER MAPPING DIFF
      // =========================================================
      const oldUserIds: number[] =
        existingGroup?.users.map((u: any) => Number(u.user_id)) || [];
  
      const newUserIds: number[] =
        (updatedUserGroup.user_ids as string[]).map((id: string) =>
          Number(id)
        );
  
      const usersToAdd: number[] = newUserIds.filter(
        (id: number) => !oldUserIds.includes(id)
      );
  
      const usersToRemove: number[] = oldUserIds.filter(
        (id: number) => !newUserIds.includes(id)
      );
  
      // ADD USERS
      await Promise.all(
        usersToAdd.map((userId: number) =>
          fetch(`${apiUrl}/role-user-mapping`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              role_id: roleId,
              user_id: userId,
              enabled: "Y",
              sys_created_by: updatedUserGroup.sys_created_by,
              sys_modified_by: updatedUserGroup.sys_modified_by,
            }),
          })
        )
      );
  
      // REMOVE USERS
      await Promise.all(
        usersToRemove.map((userId: number) =>
          fetch(
            `${apiUrl}/role-user-mapping/role/${roleId}/user/${userId}`,
            {
              method: "DELETE",
              headers,
            }
          )
        )
      );
  
      // =========================================================
      // 3️⃣ COLLECTION MAPPING DIFF
      // =========================================================
      const rcRes = await fetch(`${apiUrl}/role-collection-mapping`, {
        headers,
      });
  
      const rcData = await rcRes.json();
  
      const roleCollectionMappings: any[] = rcData.response.data.filter(
        (m: any) => m.role_id === roleId
      );

      const existingCollectionIds: string[] = roleCollectionMappings.map(
        (m: any) => String(m.collection_id)
      );
  
      const collectionsToAdd: string[] = selectedCollections.filter(
        (id: string) => !existingCollectionIds.includes(id)
      );
  
      const collectionsToRemove: any[] = roleCollectionMappings.filter(
        (m: any) => !selectedCollections.includes(String(m.collection_id))
      );
  
      // ADD COLLECTIONS
      await Promise.all(
        collectionsToAdd.map((id: string) =>
          fetch(`${apiUrl}/role-collection-mapping`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              role_id: roleId,
              collection_id: Number(id),
              domain: roleDomain,
              description: "",
              enabled: "Y",
              sys_created_by: updatedUserGroup.sys_created_by,
              sys_modified_by: updatedUserGroup.sys_modified_by,
            }),
          })
        )
      );
  
      // REMOVE COLLECTIONS (each row under the domain it was stored with)
      await Promise.all(
        collectionsToRemove.map((m: any) =>
          fetch(
            `${apiUrl}/role-collection-mapping/role/${roleId}/collection/${m.collection_id}/domain/${m.domain}`,
            {
              method: "DELETE",
              headers,
            }
          )
        )
      );
  
      // =========================================================
      // 4️⃣ POG MAPPING DIFF
      // =========================================================
      const rpRes = await fetch(`${apiUrl}/role-pog-mapping`, {
        headers,
      });
  
      const rpData = await rpRes.json();
  
      const rolePogMappings: any[] = rpData.response.data.filter(
        (m: any) => m.role_id === roleId
      );

      const existingPogIds: string[] = rolePogMappings.map((m: any) =>
        String(m.permission_object_group_id)
      );
  
      const pogsToAdd: string[] = selectedPogs.filter(
        (id: string) => !existingPogIds.includes(id)
      );
  
      const pogsToRemove: any[] = rolePogMappings.filter(
        (m: any) => !selectedPogs.includes(String(m.permission_object_group_id))
      );
  
      // ADD POGS
      await Promise.all(
        pogsToAdd.map((id: string) =>
          fetch(`${apiUrl}/role-pog-mapping`, {
            method: "POST",
            headers,
            body: JSON.stringify({
              role_id: roleId,
              permission_object_group_id: Number(id),
              domain: roleDomain,
              access_level_name: "read",
              description: "",
              enabled: "Y",
              sys_created_by: updatedUserGroup.sys_created_by,
              sys_modified_by: updatedUserGroup.sys_modified_by,
            }),
          })
        )
      );
  
      // REMOVE POGS (each row under the domain it was stored with)
      await Promise.all(
        pogsToRemove.map((m: any) =>
          fetch(
            `${apiUrl}/role-pog-mapping/role/${roleId}/pog/${m.permission_object_group_id}/domain/${m.domain}`,
            {
              method: "DELETE",
              headers,
            }
          )
        )
      );
  
      // =========================================================
      // 5️⃣ REFRESH UI
      // =========================================================
      await fetchUserGroups();
  
      setDialog({
        open: true,
        title: "Success",
        message: `Role "${updatedUserGroup.user_group_name}" successfully updated.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err) {
      handleApiError(err, "Failed to Update Role");
    }
  };

  const handleDeleteUserGroup = async (roleId: number) => {
    const headers = await getHeaders();
  
    try {
  
      // 1️⃣ DELETE ROLE-USER MAPPINGS
      const mappingRes = await fetch(`${apiUrl}/role-user-mapping`, { headers });
      const mappingData = await mappingRes.json();
  
      const roleUsers = mappingData.response.data
        .filter((m: any) => m.role_id === roleId)
        .map((m: any) => m.user_id);
  
      await Promise.all(
        roleUsers.map((userId: number) =>
          fetch(`${apiUrl}/role-user-mapping/role/${roleId}/user/${userId}`, {
            method: "DELETE",
            headers,
          })
        )
      );
  
  
      // 2️⃣ DELETE COLLECTION MAPPINGS
      const rcRes = await fetch(`${apiUrl}/role-collection-mapping`, { headers });
      const rcData = await rcRes.json();
  
      const collections = rcData.response.data.filter(
        (m: any) => m.role_id === roleId
      );
  
      await Promise.all(
        collections.map((m: any) =>
          fetch(
            `${apiUrl}/role-collection-mapping/role/${roleId}/collection/${m.collection_id}/domain/${m.domain}`,
            {
              method: "DELETE",
              headers,
            }
          )
        )
      );
  
  
      // 3️⃣ DELETE POG MAPPINGS
      const rpRes = await fetch(`${apiUrl}/role-pog-mapping`, { headers });
      const rpData = await rpRes.json();
  
      const pogs = rpData.response.data.filter(
        (m: any) => m.role_id === roleId
      );
  
      await Promise.all(
        pogs.map((m: any) =>
          fetch(`${apiUrl}/role-pog-mapping/role/${roleId}/pog/${m.permission_object_group_id}/domain/${m.domain}`, {
            method: "DELETE",
            headers,
          })
        )
      );
  
  
      // 4️⃣ DELETE ROLE
      const deleteRole = await fetch(`${apiUrl}/role/${roleId}`, {
        method: "DELETE",
        headers,
      });
  
      if (!deleteRole.ok) {
        throw new Error("Failed to delete role");
      }
  
  
      // 5️⃣ REFRESH UI
      await fetchUserGroups();
  
      setSelectedUserGroup(null);
      setSelectedRow(null);
      setIsFormDirty(false);
      setIsCreateMode(false);
  
      setDialog({
        open: true,
        title: "Deleted",
        message: "Role deleted successfully.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
    } catch (err) {
      handleApiError(err, "Failed to Delete Role");
    }
  };
  

  // const handleRowSelect = (userGroup: any) => {
  //   // setSelectedUserGroup(userGroup);
  //   // setSelectedRow((prev) => (prev === userGroup.user_group_name ? null : userGroup.user_group_name));
  //     setSelectedUserGroup(userGroup);
  // setSelectedRow(userGroup.user_group_name);
  // };
  // const handleRowSelect = (userGroup: any) => {
  //   const proceedWithSelection = () => {
  //     setIsFormDirty(false);
  //     setIsCreateMode(false);
  //     setSelectedUserGroup(userGroup);
  //     setSelectedRow((prev) =>
  //       prev === userGroup.user_group_name ? null : userGroup.user_group_name
  //     );
  //   };
  //     setSelectedUserGroup(userGroup);
  //     setSelectedRow(userGroup.user_group_name);

  //   if (isFormDirty || isCreateMode) {
  //     setDialog({
  //       open: true,
  //       title: "Unsaved Changes",
  //       message: "You have unsaved changes. Are you sure you want to discard them?",
  //       confirmButtonText: "Yes",
  //       cancelButtonText: "No",
  //       onConfirm: () => {
  //         proceedWithSelection();
  //         closeDialog();
  //       },
  //       onCancel: closeDialog,
  //     });
  //   } else {
  //     proceedWithSelection();
  //   }
  // };
  const handleRowSelect = (userGroup: any) => {
    if (isFormDirty || isCreateMode) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setIsCreateMode(false);
          setSelectedUserGroup(userGroup);
          setSelectedRow(userGroup.user_group_name);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedUserGroup(userGroup);
      setSelectedRow(userGroup.user_group_name);
    }
  };

  // const handleClearSelection = () => {
  //   setSelectedRow(null);
  //   setSelectedUserGroup(null);
  // };
  const handleClearSelection = () => {
    setIsFormDirty(false);
    setSelectedUserGroup(null);
    setSelectedRow(null);
  };

  const split = useResizableSplit("xl");
  return (
    // <div className="flex flex-col lg:flex-row bg-gray-100 p-4 min-h-full gap-4">
    //   <div className="flex-grow flex flex-col lg:basis-1/2">
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>
      {/* <div className="flex-grow flex flex-col lg:basis-1/2 xl:self-stretch bg-white"> */}
      <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
            <LoadingStyle />
          </div>
        )}
        <UserGroupTable
          userGroups={userGroups}
          onEditUserGroup={handleSaveUserGroup}
          onRowSelect={handleRowSelect}
          onClearSelection={handleClearSelection}
          selectedRow={selectedRow}
        />
      </div>

      {/* <div className="flex-grow flex flex-col lg:basis-1/3"> */}
      <SplitDivider {...split.dividerProps} />
      <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">
      {/* <div className="flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white"> */}


        <UserGroupForm
          onSaveUserGroup={handleCreateUserGroup}
          selectedUserGroup={selectedUserGroup}
          onSaveChanges={handleSaveUserGroup}
          onDeleteUserGroup={handleDeleteUserGroup}
          onClearSelection={handleClearSelection}
          userGroups={userGroups}
          onDirtyChange={setIsFormDirty}
          onModeChange={(mode) => setIsCreateMode(mode === "create")}
          selectedCollections={selectedCollections}
          selectedPogs={selectedPogs}
          onCollectionChange={setSelectedCollections}
          onPogChange={setSelectedPogs}
          canDelete={canDelete}
        />
      </div>

      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          onConfirm={dialog.onConfirm}
          cancelButtonText={dialog.cancelButtonText}
          onCancel={dialog.onCancel}
          onClose={closeDialog}
        />
      )}
      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default UserGroupSetup;
